import { PageHeader } from "@bednbody/ui/components/page-header";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@bednbody/ui/components/toggle-group";
import React, { useMemo, useState } from "react";
import { isOutstandingCash } from "@/features/cash/cash.custody";
import {
  PulseIcon,
  WarningCircleIcon,
  MoneyIcon,
  ChartBarIcon,
  BicycleIcon,
  CalendarCheckIcon,
  CalendarDotsIcon,
  CheckCircleIcon,
  FlameIcon,
  SquaresFourIcon,
  ListNumbersIcon,
  MapPinIcon,
  RadioIcon,
  PaperPlaneRightIcon,
  SparkleIcon,
  UserCheckIcon,
  UsersIcon,
  UsersThreeIcon,
  WalletIcon,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { isHubScoped, type StaffRole } from "@/lib/jwt-claims";
import { canAccess, hrefFor } from "@/lib/permissions";
import { minutesSince } from "@/lib/time";
import {
  useAttendance,
  useBookingPerformanceStats,
  useCashRemittances,
  useHubsList,
  useHubStaff,
  useNoShowReports,
  useOpsBookings,
  useWeeklyBookingStats,
  type BookingStatsRange,
  EMPTY_FILTERS,
} from "./ops-dashboard.hooks";
import {
  IN_FLIGHT_STATUSES,
  PRE_SESSION_STATUSES,
  useAllCashRemittances,
  useAsapQueue,
  useBookingEventsFeed,
  useCommandBookings,
  useDispatchQueue,
  useOngoingTrips,
  useOpsRealtime,
  useOpsWindow,
} from "./command-center.hooks";
import { RecentBookingsList } from "./components/RecentBookingsList";
import { TeamBoard } from "./components/TeamBoard";
import { StatCard } from "./components/StatCard";
import { SectionCard } from "./components/SectionCard";
import { NeedsAttention, type AttentionItem } from "./components/NeedsAttention";
import { toAttentionItems, usePaymentExceptions } from "./payment-exceptions.hooks";
import { MoneyFlowCard } from "./components/MoneyFlowCard";
import { AttentionStrip } from "./components/AttentionStrip";
import { useOpsAttention } from "./ops-attention.hooks";
import { TrendsCard } from "./components/TrendsCard";
import { StaleDataBanner } from "./components/StaleDataBanner";
import { KpiCard } from "./components/KpiCard";
import { LiveOpsTable } from "./components/LiveOpsTable";
import { FleetStatusDonut } from "./components/FleetStatusDonut";
import { DispatchQueueCard } from "./components/DispatchQueueCard";
import { TherapistQueueCard } from "./components/TherapistQueueCard";
import { StaffAvailabilityList } from "./components/StaffAvailabilityList";
import { ActivityFeed } from "./components/ActivityFeed";
import { OngoingTripsCard } from "./components/OngoingTripsCard";
import { MiniAnalytics } from "./components/MiniAnalytics";
import { BusinessPerformanceChart } from "./components/BusinessPerformanceChart";
import { WeeklyBookingsChart } from "./components/WeeklyBookingsChart";
import { BusyHoursHeatmap } from "./components/BusyHoursHeatmap";
import { HubPerformanceGrid } from "./components/HubPerformanceGrid";
import { CashSummaryCard } from "./components/CashSummaryCard";
import { OpsSignalsCard } from "./components/OpsSignalsCard";
import { CollapsibleSection } from "./components/CollapsibleSection";
import { QuickActionsBar } from "./components/QuickActionsBar";

function greetingForHour(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

/** No display name in the JWT claims (BednbodyClaims is deliberately just
 * role/hub_id) — derived from the session email instead of adding a profile
 * fetch just for a greeting. */
function nameFromEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const local = email.split("@")[0];
  if (!local) return null;
  return local.charAt(0).toUpperCase() + local.slice(1);
}

/** % change vs yesterday; null when yesterday is zero (no honest baseline). */
function deltaPct(today: number, yesterday: number | undefined): number | null {
  if (yesterday == null || yesterday === 0) return null;
  return ((today - yesterday) / yesterday) * 100;
}

/**
 * ToggleGroup item values are strings; `BookingStatsRange` mixes a literal with
 * numbers. Parsed rather than cast, so a typo in a ToggleGroupItem's `value`
 * falls back to "today" instead of reaching the query as NaN.
 */
function performanceRangeFrom(raw: string): BookingStatsRange {
  if (raw === "7") return 7;
  if (raw === "30") return 30;
  return "today";
}

/** One-line subtitle matching what this role's dashboard actually shows. */
function subtitleForRole(role: StaffRole): string {
  switch (role) {
    case "hub_manager":
      return "Here's what's happening at your hub today.";
    case "hr":
      return "People operations — attendance, roster, and payroll at a glance.";
    case "executive":
      return "Command center — live view across all hubs (read-only).";
    case "superadmin":
      return "Business overview — the operational decisions that need you today.";
    default:
      return "Command center — live operations across all hubs.";
  }
}

export function OpsDashboardPage() {
  const { claims, session } = useAuth();
  const hubScoped = claims ? isHubScoped(claims.role) : false;
  const hubId = claims?.hub_id ?? null;
  const displayName = nameFromEmail(session?.user.email);

  return (
    <div className="space-y-8">
      <PageHeader
        actions={
          <div className="flex items-center gap-2 type-body-small text-muted-foreground">
            <span className="relative flex size-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-success" />
            </span>
            Live · auto-refreshing
          </div>
        }
        description={claims ? subtitleForRole(claims.role) : ""}
        title={
          <>
            {greetingForHour(new Date().getHours())}
            {displayName ? `, ${displayName}` : ""}
          </>
        }
      />

      {claims ? (
        hubScoped ? (
          <HubDashboard hubId={hubId} />
        ) : claims.role === "hr" ? (
          <HrDashboard />
        ) : claims.role === "superadmin" ? (
          <SuperadminDashboard />
        ) : (
          <CommandCenter role={claims.role} />
        )
      ) : null}

      {claims && claims.role !== "superadmin" ? <QuickActionsBar role={claims.role} /> : null}
    </div>
  );
}

/**
 * Superadmin is a business-control surface, not an expanded version of the
 * dispatch console. It keeps the page to the questions an owner needs answered
 * quickly: is today on track, what needs intervention, is the field staffed,
 * and which hub needs a closer look. Detail remains in dedicated workflows.
 */
function SuperadminDashboard() {
  useOpsRealtime(true);

  const [performanceRange, setPerformanceRange] = useState<BookingStatsRange>("today");
  const bookingsQuery = useCommandBookings();
  const queueQuery = useDispatchQueue();
  const { data: bookings, isLoading: bookingsLoading } = bookingsQuery;
  const { data: queue, isLoading: queueLoading } = queueQuery;
  const { data: trips, isLoading: tripsLoading } = useOngoingTrips();
  const { data: remittances, isLoading: remittancesLoading } = useAllCashRemittances();
  const { data: weekly } = useWeeklyBookingStats(null);
  const { data: performance, isLoading: performanceLoading } = useBookingPerformanceStats(
    null,
    performanceRange,
  );
  const attendanceQuery = useAttendance(null);
  const { data: attendance, isLoading: attendanceLoading } = attendanceQuery;
  const { data: hubs } = useHubsList();

  const allBookings = useMemo(() => bookings ?? [], [bookings]);
  const allQueue = useMemo(() => queue ?? [], [queue]);
  const allAttendance = useMemo(() => attendance ?? [], [attendance]);
  const today = new Date().toDateString();

  const summary = useMemo(() => {
    const todays = allBookings.filter(
      (booking) => new Date(booking.scheduled_at).toDateString() === today,
    );
    const bookedValueToday = todays
      .filter((booking) => booking.status !== "cancelled")
      .reduce((total, booking) => total + booking.gross_amount, 0);
    const field = allAttendance.filter(
      (member) => member.role === "therapist" || member.role === "rider",
    );
    const yesterday = (weekly ?? []).at(-2);

    return {
      bookingsToday: todays.length,
      bookedValueToday,
      activeSessions: allBookings.filter((booking) =>
        (IN_FLIGHT_STATUSES as readonly string[]).includes(booking.status),
      ).length,
      awaitingDispatch: allBookings.filter(
        (booking) => booking.status === "pending" || booking.status === "awaiting_rider",
      ).length,
      fieldOnDuty: field.filter((member) => member.attendance?.onDuty).length,
      fieldTotal: field.length,
      ridersAvailable: allQueue.filter((rider) => rider.status === "available").length,
      ridersTotal: allQueue.length,
      hubsWithWorkToday: new Set(todays.map((booking) => booking.hub_id)).size,
      bookingDelta: deltaPct(todays.length, yesterday?.bookings),
      valueDelta: deltaPct(bookedValueToday, yesterday?.bookedValue),
    };
  }, [allAttendance, allBookings, allQueue, today, weekly]);

  const todaysBookings = useMemo(
    () => allBookings.filter((booking) => new Date(booking.scheduled_at).toDateString() === today),
    [allBookings, today],
  );
  const attention = useOpsAttention(bookings, bookingsQuery.isError, true);

  const kpisLoading = bookingsLoading || queueLoading || attendanceLoading;

  // The two queries every card on this page ultimately reads. If either is in
  // error the numbers below are not a picture of the floor, and must not be
  // presented as one.
  const liveDataFailed = bookingsQuery.isError || queueQuery.isError;
  const attendanceFailed = attendanceQuery.isError;
  const retryLiveData = () => {
    void bookingsQuery.refetch();
    void queueQuery.refetch();
  };

  return (
    <React.Fragment>
      <StaleDataBanner
        failed={liveDataFailed}
        onRetry={retryLiveData}
        isRetrying={bookingsQuery.isFetching || queueQuery.isFetching}
      />
      {/* BL-38 — present only when something needs action (or a check failed). */}
      <AttentionStrip items={attention.items} failed={attention.failed} />

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiCard
          label="Booked value today"
          value={summary.bookedValueToday}
          prefix="₱"
          icon={WalletIcon}
          deltaPct={summary.valueDelta}
          spark={(weekly ?? []).map((day) => day.bookedValue)}
          isLoading={kpisLoading}
        />
        <KpiCard
          label="Scheduled today"
          value={summary.bookingsToday}
          icon={CalendarCheckIcon}
          deltaPct={summary.bookingDelta}
          spark={(weekly ?? []).map((day) => day.bookings)}
          isLoading={kpisLoading}
        />
        <KpiCard
          label="Active sessions"
          value={summary.activeSessions}
          icon={PulseIcon}
          hint="in progress now"
          isLoading={kpisLoading}
        />
        <KpiCard
          label="Awaiting dispatch"
          value={summary.awaitingDispatch}
          icon={PaperPlaneRightIcon}
          tone={summary.awaitingDispatch > 0 ? "warning" : "default"}
          hint="needs a rider or review"
          isLoading={kpisLoading}
        />
      </div>

      {/* BL-37 — today's bookings take the Priority queue's place; its signal
          now lives in the strip above and the Attention column (BL-20). */}
      <SectionCard title="Today's bookings" icon={CalendarDotsIcon} viewAllHref="/bookings">
        <LiveOpsTable
          bookings={todaysBookings}
          isLoading={bookingsLoading}
          attentionByBookingId={attention.byBookingId}
          sosRefs={attention.sosRefs}
          emptyDescription="Nothing is scheduled for today yet."
          exportFilename="todays-bookings"
        />
      </SectionCard>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SectionCard title="Field readiness" icon={UsersIcon} viewAllHref="/attendance">
          {kpisLoading || tripsLoading ? (
            <Skeleton className="h-28 motion-reduce:animate-none" />
          ) : (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Field team on duty</dt>
                <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                  {summary.fieldOnDuty}/{summary.fieldTotal}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Riders available</dt>
                <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                  {summary.ridersAvailable}/{summary.ridersTotal}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Riders on the road</dt>
                <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                  {trips?.length ?? 0}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Hubs with work today</dt>
                <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
                  {summary.hubsWithWorkToday}
                </dd>
              </div>
            </dl>
          )}
        </SectionCard>
        <SectionCard title="Cash pipeline" icon={MoneyIcon} viewAllHref="/cash">
          <CashSummaryCard
            remittances={remittances ?? []}
            isLoading={remittancesLoading}
            canOpenCash
          />
        </SectionCard>
      </div>

      <SectionCard
        title="Business performance"
        icon={ChartBarIcon}
        viewAllHref="/analytics"
        actions={
          <ToggleGroup
            aria-label="Business performance period"
            className="w-full sm:w-auto"
            size="sm"
            spacing={1}
            value={[String(performanceRange)]}
            onValueChange={(values) => {
              // ToggleGroup hands back an ARRAY of pressed values. `.at(-1)`
              // takes the most recent one and, unlike `[0]`, does not read a
              // stale first element when the group can empty itself by
              // unpressing the active chip. Undefined means "nothing
              // pressed", which we ignore rather than clearing the range.
              const next = values.at(-1);
              if (next !== undefined) setPerformanceRange(performanceRangeFrom(next));
            }}
          >
            <ToggleGroupItem value="today">Today</ToggleGroupItem>
            <ToggleGroupItem value="7">7 days</ToggleGroupItem>
            <ToggleGroupItem value="30">30 days</ToggleGroupItem>
          </ToggleGroup>
        }
      >
        <BusinessPerformanceChart
          period={performance ?? []}
          range={performanceRange}
          isLoading={performanceLoading}
        />
      </SectionCard>

      <SectionCard title="Hub health" icon={SquaresFourIcon} viewAllHref="/hubs">
        <HubPerformanceGrid
          hubs={hubs ?? []}
          bookings={allBookings}
          attendance={allAttendance}
          isLoading={bookingsLoading || attendanceLoading}
          failed={bookingsQuery.isError || attendanceFailed}
        />
      </SectionCard>
    </React.Fragment>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Command center — cross-hub roles (admin / executive / superadmin) only.
 * Client names, booking_events, and rider GPS summaries are admin-scoped
 * reads a hub_manager structurally cannot make (data-wall.md).             */
/* ────────────────────────────────────────────────────────────────────────── */

function CommandCenter({ role }: { role: StaffRole }) {
  useOpsRealtime(true);

  const bookingsQuery = useCommandBookings();
  const queueQuery = useDispatchQueue();
  const { data: bookings, isLoading: bookingsLoading } = bookingsQuery;
  const { data: queue, isLoading: queueLoading } = queueQuery;
  const { data: asapQueue, isLoading: asapQueueLoading } = useAsapQueue();
  const { data: trips, isLoading: tripsLoading } = useOngoingTrips();
  const eventsQuery = useBookingEventsFeed();
  const remittancesQuery = useAllCashRemittances();
  const { data: events, isLoading: eventsLoading } = eventsQuery;
  const { data: remittances, isLoading: remittancesLoading } = remittancesQuery;
  const { data: opsWindow, isLoading: windowLoading } = useOpsWindow();
  const { data: weekly } = useWeeklyBookingStats(null);
  const attendanceQuery = useAttendance(null);
  const { data: attendance, isLoading: attendanceLoading } = attendanceQuery;
  const { data: hubs } = useHubsList();
  // RLS scopes this to the caller's hub for a Hub Manager, same as the
  // bookings feed — no client-side hub filter needed.
  const { data: paymentExceptions } = usePaymentExceptions();

  const allBookings = useMemo(() => bookings ?? [], [bookings]);
  const allQueue = useMemo(() => queue ?? [], [queue]);
  const allAsapQueue = useMemo(() => asapQueue ?? [], [asapQueue]);
  // BL-20 — the Live Operations table's Attention column, from the same engine
  // as the live map.
  const attention = useOpsAttention(bookings, bookingsQuery.isError, role === "superadmin");
  const allAttendance = useMemo(() => attendance ?? [], [attendance]);

  const kpis = useMemo(() => {
    const today = new Date().toDateString();
    const todays = allBookings.filter((b) => new Date(b.scheduled_at).toDateString() === today);
    const completedToday = todays.filter((b) => b.status === "completed");
    const bookedValueToday = todays
      .filter((b) => b.status !== "cancelled")
      .reduce((sum, b) => sum + b.gross_amount, 0);

    const active = allBookings.filter((b) =>
      (IN_FLIGHT_STATUSES as readonly string[]).includes(b.status),
    ).length;
    const pendingDispatch = allBookings.filter(
      (b) => b.status === "pending" || b.status === "awaiting_rider",
    ).length;

    const ridersAvailable = allQueue.filter((r) => r.status === "available").length;
    const field = allAttendance.filter((a) => a.role === "therapist" || a.role === "rider");
    const onDuty = field.filter((a) => a.attendance?.onDuty).length;

    const yesterday = (weekly ?? []).at(-2);
    return {
      todayCount: todays.length,
      completedToday: completedToday.length,
      bookedValueToday,
      active,
      pendingDispatch,
      ridersAvailable,
      ridersTotal: allQueue.length,
      onDuty,
      fieldTotal: field.length,
      deltaBookings: deltaPct(todays.length, yesterday?.bookings),
      deltaCompleted: deltaPct(completedToday.length, yesterday?.completed),
      deltaValue: deltaPct(bookedValueToday, yesterday?.bookedValue),
    };
  }, [allBookings, allQueue, allAttendance, weekly]);

  const sparks = useMemo(
    () => ({
      bookings: (weekly ?? []).map((d) => d.bookings),
      completed: (weekly ?? []).map((d) => d.completed),
      value: (weekly ?? []).map((d) => d.bookedValue),
    }),
    [weekly],
  );

  // Emergency-tier items: >30 min past schedule with no session start, and
  // bookings stuck without a rider. No SOS/breakdown tables exist in the
  // schema — those would be invented data (never-invent-policy.md).
  const dispatchHref = hrefFor(role, "/dispatch");
  const attentionItems = useMemo<AttentionItem[]>(() => {
    // Payment exceptions lead: every other item is work running late, these
    // are money that already moved with nothing following it (0093).
    const items: AttentionItem[] = toAttentionItems(paymentExceptions ?? []);
    for (const b of allBookings) {
      const late = minutesSince(b.scheduled_at);
      if ((PRE_SESSION_STATUSES as readonly string[]).includes(b.status) && late > 30) {
        items.push({
          id: `late-${b.id}`,
          tag: "overdue",
          tone: "danger",
          text: `${b.ref} (${b.hubs?.name ?? "?"}): ${late} min past schedule, session not started`,
          href: dispatchHref,
        });
      } else if (b.status === "awaiting_rider") {
        items.push({
          id: `rider-${b.id}`,
          tag: "rider",
          tone: "warning",
          text: `${b.ref} (${b.hubs?.name ?? "?"}): no rider assigned`,
          at: b.scheduled_at,
          href: dispatchHref,
        });
      }
    }
    return items.sort((a, b) => (a.tone === "danger" ? -1 : 1) - (b.tone === "danger" ? -1 : 1));
  }, [allBookings, dispatchHref, paymentExceptions]);

  const kpisLoading = bookingsLoading || queueLoading || attendanceLoading;

  // The two queries every card on this page ultimately reads. If either is in
  // error the numbers below are not a picture of the floor, and must not be
  // presented as one.
  const liveDataFailed = bookingsQuery.isError || queueQuery.isError;
  const attendanceFailed = attendanceQuery.isError;
  const retryLiveData = () => {
    void bookingsQuery.refetch();
    void queueQuery.refetch();
  };

  return (
    <>
      <StaleDataBanner
        failed={liveDataFailed}
        onRetry={retryLiveData}
        isRetrying={bookingsQuery.isFetching || queueQuery.isFetching}
      />
      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 2xl:grid-cols-8">
        <KpiCard
          label="Bookings today"
          value={kpis.todayCount}
          icon={CalendarCheckIcon}
          deltaPct={kpis.deltaBookings}
          spark={sparks.bookings}
          isLoading={kpisLoading}
        />
        <KpiCard
          label="Completed today"
          value={kpis.completedToday}
          icon={CheckCircleIcon}
          deltaPct={kpis.deltaCompleted}
          spark={sparks.completed}
          tone="success"
          isLoading={kpisLoading}
        />
        <KpiCard
          label="Booked value today"
          value={kpis.bookedValueToday}
          prefix="₱"
          icon={WalletIcon}
          deltaPct={kpis.deltaValue}
          spark={sparks.value}
          isLoading={kpisLoading}
        />
        <KpiCard
          label="Active sessions"
          value={kpis.active}
          icon={PulseIcon}
          hint="in flight now"
          isLoading={kpisLoading}
        />
        <KpiCard
          label="Ongoing trips"
          value={trips?.length ?? 0}
          icon={MapPinIcon}
          hint="riders on the road"
          isLoading={tripsLoading}
        />
        <KpiCard
          label="Riders available"
          value={`${kpis.ridersAvailable}/${kpis.ridersTotal}`}
          icon={BicycleIcon}
          tone={kpis.ridersAvailable === 0 && kpis.pendingDispatch > 0 ? "danger" : "default"}
          isLoading={kpisLoading}
        />
        <KpiCard
          label="Staff on duty"
          value={`${kpis.onDuty}/${kpis.fieldTotal}`}
          icon={UserCheckIcon}
          isLoading={kpisLoading}
        />
        <KpiCard
          label="Pending dispatch"
          value={kpis.pendingDispatch}
          icon={PaperPlaneRightIcon}
          tone={kpis.pendingDispatch > 0 ? "warning" : "default"}
          isLoading={kpisLoading}
        />
      </div>

      {/* Live ops table + right rail */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr]">
        <CollapsibleSection
          id="live-ops"
          title="Live Operations"
          icon={SquaresFourIcon}
          viewAllHref={hrefFor(role, "/bookings")}
        >
          <LiveOpsTable
            bookings={allBookings}
            isLoading={bookingsLoading}
            attentionByBookingId={attention.byBookingId}
            sosRefs={attention.sosRefs}
          />
        </CollapsibleSection>

        <div className="space-y-4">
          <CollapsibleSection
            id="attention"
            title="Needs Attention"
            icon={WarningCircleIcon}
            badge={attentionItems.length > 0 ? `${attentionItems.length}` : undefined}
          >
            <NeedsAttention items={attentionItems} maxItems={4} failed={liveDataFailed} />
          </CollapsibleSection>
          <CollapsibleSection id="fleet" title="Rider Fleet" icon={BicycleIcon}>
            <FleetStatusDonut
              queue={allQueue}
              isLoading={queueLoading}
              failed={queueQuery.isError}
            />
          </CollapsibleSection>
          <CollapsibleSection
            id="dispatch-queue"
            title="Dispatch Queue"
            icon={ListNumbersIcon}
            viewAllHref={dispatchHref}
          >
            <DispatchQueueCard
              bookings={allBookings}
              queue={allQueue}
              isLoading={bookingsLoading || queueLoading}
              failed={liveDataFailed}
              canDispatch={canAccess(role, "/dispatch")}
            />
          </CollapsibleSection>
          <CollapsibleSection id="asap-queue" title="Therapist Queue" icon={UsersThreeIcon}>
            <TherapistQueueCard queue={allAsapQueue} isLoading={asapQueueLoading} />
          </CollapsibleSection>
        </div>
      </div>

      {/* Field ops row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <CollapsibleSection
          id="trips"
          title="On the Road"
          icon={RadioIcon}
          viewAllHref={hrefFor(role, "/live-map")}
        >
          <OngoingTripsCard
            trips={trips ?? []}
            isLoading={tripsLoading}
            canOpenMap={canAccess(role, "/live-map")}
          />
        </CollapsibleSection>
        <CollapsibleSection
          id="staff"
          title="Staff Availability"
          icon={UsersIcon}
          viewAllHref={hrefFor(role, "/attendance")}
        >
          <StaffAvailabilityList
            attendance={allAttendance}
            isLoading={attendanceLoading}
            failed={attendanceFailed}
          />
        </CollapsibleSection>
        <CollapsibleSection id="feed" title="Activity Feed" icon={PulseIcon}>
          <ActivityFeed
            events={events ?? []}
            remittances={remittances ?? []}
            attendance={allAttendance}
            isLoading={eventsLoading || remittancesLoading}
            failed={eventsQuery.isError || remittancesQuery.isError || attendanceFailed}
          />
        </CollapsibleSection>
      </div>

      {/* Analytics row */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr]">
        <CollapsibleSection
          id="weekly-trend"
          title="7-Day Booking Trend"
          icon={ChartBarIcon}
          viewAllHref={hrefFor(role, "/analytics")}
        >
          <WeeklyBookingsChart weekly={weekly ?? []} isLoading={!weekly} />
        </CollapsibleSection>
        <CollapsibleSection
          id="analytics"
          title="Today at a Glance"
          icon={ChartBarIcon}
          viewAllHref={hrefFor(role, "/analytics")}
        >
          <MiniAnalytics window={opsWindow ?? []} weekly={weekly ?? []} isLoading={windowLoading} />
        </CollapsibleSection>
        <CollapsibleSection
          id="cash"
          title="Cash Summary"
          icon={MoneyIcon}
          viewAllHref={hrefFor(role, "/cash")}
        >
          <CashSummaryCard
            remittances={remittances ?? []}
            isLoading={remittancesLoading}
            canOpenCash={canAccess(role, "/cash")}
          />
        </CollapsibleSection>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr]">
        <CollapsibleSection id="heatmap" title="Busy Hours" icon={FlameIcon}>
          <BusyHoursHeatmap window={opsWindow ?? []} isLoading={windowLoading} />
        </CollapsibleSection>
        <CollapsibleSection id="signals" title="Operational Signals" icon={SparkleIcon}>
          <OpsSignalsCard
            bookings={allBookings}
            weekly={weekly ?? []}
            queue={allQueue}
            attendance={allAttendance}
          />
        </CollapsibleSection>
      </div>

      {/* Hub scoreboard */}
      <CollapsibleSection
        id="hubs"
        title="Hub Performance"
        icon={SquaresFourIcon}
        viewAllHref={hrefFor(role, "/hubs")}
      >
        <HubPerformanceGrid
          hubs={hubs ?? []}
          bookings={allBookings}
          attendance={allAttendance}
          isLoading={bookingsLoading || attendanceLoading}
          failed={bookingsQuery.isError || attendanceFailed}
        />
      </CollapsibleSection>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* HR dashboard — people operations only. hr has NO grant on dispatch_queue,
 * trips, or booking_events (0005/0006), so the command-center hooks would
 * error for this role; everything here reads staff / attendance / bookings /
 * hubs, all of which hr may select.                                          */
/* ────────────────────────────────────────────────────────────────────────── */

function HrDashboard() {
  const attendanceQuery = useAttendance(null);
  const { data: attendance, isLoading: attendanceLoading } = attendanceQuery;
  const attendanceFailed = attendanceQuery.isError;
  const { data: weekly } = useWeeklyBookingStats(null);

  const allAttendance = useMemo(() => attendance ?? [], [attendance]);

  const stats = useMemo(() => {
    const field = allAttendance.filter((a) => a.role === "therapist" || a.role === "rider");
    const onSession = field.filter((a) => a.attendance?.onDuty).length;
    // ⚠ THE DENOMINATOR IS WHO HOLDS A SESSION, NOT THE WHOLE ROSTER. A
    // therapist on a finalized Off date is not absent — she is off, and
    // counting her against "Absent today" reported most of the hub as missing
    // every day. `off` is the only state that means "not expected".
    const scheduled = field.filter((a) => a.attendance !== null && a.attendance.state !== "off");
    // Absent is the recorded No-Show plus, inside the window, anyone past their
    // start who has not begun. Both are people who did not turn up; the second
    // can still fix it, which is why the Attendance page separates them.
    const missing = scheduled.filter(
      (a) => a.attendance?.outcome === "no_show" || a.attendance?.runningLate,
    ).length;
    const today = (weekly ?? []).at(-1);
    const yesterday = (weekly ?? []).at(-2);
    return {
      fieldTotal: field.length,
      onSession,
      scheduledToday: scheduled.length,
      missing,
      bookingsToday: today?.bookings ?? 0,
      deltaBookings: deltaPct(today?.bookings ?? 0, yesterday?.bookings),
    };
  }, [allAttendance, weekly]);

  return (
    <>
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiCard
          label="Staff on session"
          value={`${stats.onSession}/${stats.scheduledToday}`}
          icon={UserCheckIcon}
          isLoading={attendanceLoading}
        />
        <KpiCard
          label="Scheduled today"
          value={stats.scheduledToday}
          icon={UsersIcon}
          isLoading={attendanceLoading}
        />
        <KpiCard
          label="Bookings today"
          value={stats.bookingsToday}
          icon={CalendarCheckIcon}
          deltaPct={stats.deltaBookings}
          spark={(weekly ?? []).map((d) => d.bookings)}
          isLoading={attendanceLoading}
        />
        <KpiCard
          label="Absent today"
          value={stats.missing}
          icon={WarningCircleIcon}
          tone={stats.missing > 0 ? "warning" : "default"}
          isLoading={attendanceLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CollapsibleSection
          id="hr-staff"
          title="Staff Availability"
          icon={UsersIcon}
          viewAllHref="/roster"
        >
          <StaffAvailabilityList
            attendance={allAttendance}
            isLoading={attendanceLoading}
            failed={attendanceFailed}
          />
        </CollapsibleSection>
        <CollapsibleSection id="hr-feed" title="Today's Clock-ins" icon={PulseIcon}>
          <ActivityFeed
            events={[]}
            remittances={[]}
            attendance={allAttendance}
            isLoading={attendanceLoading}
            failed={attendanceFailed}
          />
        </CollapsibleSection>
      </div>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Hub-manager dashboard — unchanged one-screen grid. Its reads stay inside
 * the hub_manager column grants; it must NOT adopt the command-center hooks
 * (client-name joins / booking_events would violate the data wall).         */
/* ────────────────────────────────────────────────────────────────────────── */

function HubDashboard({ hubId }: { hubId: string | null }) {
  const bookingsQuery = useOpsBookings({ ...EMPTY_FILTERS, hubId });
  const { data: allBookings, isLoading } = bookingsQuery;
  const liveDataFailed = bookingsQuery.isError;
  const { data: attendance } = useAttendance(hubId);
  const { data: staff } = useHubStaff(hubId);
  const { data: remittances } = useCashRemittances(hubId);
  const { data: noShows } = useNoShowReports(hubId);
  const { data: weeklyStats } = useWeeklyBookingStats(hubId);

  const hubStats = useMemo(() => {
    if (!allBookings) return null;
    const active = allBookings.filter((b) =>
      (IN_FLIGHT_STATUSES as readonly string[]).includes(b.status),
    ).length;
    const today = allBookings.filter(
      (b) => new Date(b.scheduled_at).toDateString() === new Date().toDateString(),
    ).length;
    return { active, today };
  }, [allBookings]);

  // weeklyStats is chronological: last entry = today, second-to-last = yesterday.
  const yesterdayCount = weeklyStats?.at(-2)?.bookings ?? null;

  const roster = useMemo(() => {
    const eligible = (staff ?? []).filter((s) => s.role === "therapist" || s.role === "rider");
    const onSession = (attendance ?? []).filter((a) => a.attendance?.onDuty).length;
    return { total: eligible.length, onSession };
  }, [staff, attendance]);

  // ⚠ `isOutstandingCash`, NOT `status === "pending"`. This drives an ATTENTION
  // ITEM, and since 0203 a ₱0 remittance opens on every cash booking where the
  // Therapist kept the lot — never instructed, never receivable, `pending` for
  // ever. On the old predicate the dashboard raised "5 cash remittances waiting
  // to be received (₱0)" permanently, with no action that could clear it.
  const pendingRemittances = useMemo(
    () => (remittances ?? []).filter(isOutstandingCash),
    [remittances],
  );
  const pendingCashTotal = pendingRemittances.reduce((sum, r) => sum + r.amount, 0);

  const attentionItems = useMemo<AttentionItem[]>(() => {
    const items: AttentionItem[] = [];

    for (const b of allBookings ?? []) {
      if (b.status === "awaiting_rider") {
        items.push({
          id: `rider-${b.id}`,
          tag: "rider",
          tone: "danger",
          text: `${b.ref}: no rider assigned yet`,
          at: b.scheduled_at,
          href: "/dispatch",
        });
      } else if (
        (PRE_SESSION_STATUSES as readonly string[]).includes(b.status) &&
        minutesSince(b.scheduled_at) > 0
      ) {
        items.push({
          id: `late-${b.id}`,
          tag: "overdue",
          tone: "warning",
          text: `${b.ref}: session not started, ${minutesSince(b.scheduled_at)} min past schedule`,
          href: "/dispatch",
        });
      }
    }

    for (const report of noShows ?? []) {
      items.push({
        id: `nsr-${report.id}`,
        tag: "no-show",
        tone: "danger",
        text: `${report.bookings?.ref ?? "Booking"}: ${report.party} reported as no-show`,
        at: report.created_at ?? undefined,
        href: "/dispatch",
      });
    }

    if (pendingRemittances.length > 0) {
      items.push({
        id: "cash-pending",
        tag: "cash",
        tone: "warning",
        text: `${pendingRemittances.length} cash remittance${pendingRemittances.length > 1 ? "s" : ""} waiting to be received (₱${pendingCashTotal.toLocaleString()})`,
        href: "/cash",
      });
    }

    return items;
  }, [allBookings, noShows, pendingRemittances, pendingCashTotal]);

  const todaysBookings = useMemo(
    () =>
      (allBookings ?? [])
        .filter((b) => new Date(b.scheduled_at).toDateString() === new Date().toDateString())
        .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)),
    [allBookings],
  );

  if (!hubStats) {
    return (
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard label="Active Sessions" value={hubStats.active} icon={PulseIcon} />
        <StatCard
          label="Today's Bookings"
          value={hubStats.today}
          icon={CalendarCheckIcon}
          hint={yesterdayCount != null ? `yesterday: ${yesterdayCount}` : undefined}
        />
        <StatCard
          label="Staff On Session"
          value={`${roster.onSession}/${roster.total}`}
          icon={UserCheckIcon}
        />
        <StatCard
          label="Pending Cash"
          value={pendingCashTotal}
          prefix="₱"
          icon={MoneyIcon}
          tone={pendingCashTotal > 0 ? "warning" : "default"}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.2fr_1fr]">
        <SectionCard title="Today's Bookings" icon={CalendarDotsIcon} viewAllHref="/bookings">
          <RecentBookingsList
            bookings={todaysBookings}
            isLoading={isLoading}
            failed={liveDataFailed}
            maxRows={5}
          />
        </SectionCard>
        <TrendsCard weekly={weeklyStats ?? []} bookings={allBookings ?? []} chartHeight={190} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <SectionCard
          title="Needs Attention"
          icon={WarningCircleIcon}
          badge={attentionItems.length > 0 ? `${attentionItems.length}` : undefined}
        >
          <NeedsAttention items={attentionItems} maxItems={4} failed={liveDataFailed} />
        </SectionCard>
        <SectionCard
          title="Money Flow"
          icon={WalletIcon}
          viewAllHref="/cash"
          badge={pendingRemittances.length > 0 ? `${pendingRemittances.length} pending` : undefined}
        >
          <MoneyFlowCard bookings={allBookings ?? []} remittances={remittances ?? []} />
        </SectionCard>
        <SectionCard
          title="Team"
          icon={UsersIcon}
          viewAllHref="/roster"
          badge={`${roster.onSession}/${roster.total} on duty`}
        >
          <TeamBoard hubId={hubId} />
        </SectionCard>
      </div>
    </>
  );
}
